"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com";

const cls = (...c) => c.filter(Boolean).join(" ");

// ── Search helpers ─────────────────────────────────────────────────────────────
function normalise(str) {
  return (str || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function tokenise(str) {
  return normalise(str)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}
function scoreItem(item, queryTokens) {
  if (!queryTokens.length) return 0;
  const haystack = [item.itemCode, item.title, item.sectionNumber, item.uom]
    .filter(Boolean)
    .map(normalise)
    .join(" ");
  let score = 0;
  for (const qt of queryTokens) {
    const code = normalise(item.itemCode || "");
    if (code === qt) {
      score += 100;
      continue;
    }
    if (code.startsWith(qt)) {
      score += 60;
      continue;
    }
    const titleTokens = tokenise(item.title || "");
    if (titleTokens.includes(qt)) {
      score += 30;
      continue;
    }
    if (haystack.includes(qt)) {
      score += 15;
    }
  }
  if (score > 0) score += 10; // full-match bonus
  return score;
}

function Highlight({ text = "", tokens = [] }) {
  if (!tokens.length) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const ranges = [];
  for (const t of tokens) {
    let idx = 0;
    while ((idx = lower.indexOf(t, idx)) !== -1) {
      ranges.push([idx, idx + t.length]);
      idx += t.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1])
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark
        key={s}
        className="bg-orange-500/30 text-orange-200 rounded-sm px-0.5"
      >
        {text.slice(s, e)}
      </mark>,
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span>{parts}</span>;
}

// ── Labour Catalogue Modal ─────────────────────────────────────────────────────
// Searches the company's configured LabourItems (live-resolved from /api/labour-items).
// Filters out items already on the project by labourItem._id.
function LabourCatalogModal({
  open,
  labourItems,
  alreadyAddedIds,
  onSelect,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const rowRefs = useRef({});

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const queryTokens = useMemo(() => tokenise(query), [query]);

  const available = useMemo(
    () =>
      labourItems.filter((item) => !alreadyAddedIds.has(item._id?.toString())),
    [labourItems, alreadyAddedIds],
  );

  const results = useMemo(() => {
    if (!queryTokens.length) {
      return [...available].sort((a, b) =>
        (a.itemCode || "").localeCompare(b.itemCode || ""),
      );
    }
    return available
      .map((item) => ({ item, score: scoreItem(item, queryTokens) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.item);
  }, [available, queryTokens]);

  useEffect(() => setActiveIdx(0), [results]);

  const scrollTo = (idx) =>
    rowRefs.current[idx]?.scrollIntoView({ block: "nearest" });

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.min(i + 1, results.length - 1);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.max(i - 1, 0);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "Enter" && results[activeIdx]) {
      onSelect(results[activeIdx]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
      <div
        className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl flex flex-col w-full max-w-2xl mx-4"
        style={{ maxHeight: "75vh" }}
        onKeyDown={handleKey}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-700 shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-white">
              Add Labour Item
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {available.length} item{available.length !== 1 ? "s" : ""}{" "}
              available · select one to add to this project
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-700 shrink-0">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by code, description, section…"
              className="w-full pl-9 pr-8 py-2 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-sm"
              >
                ×
              </button>
            )}
          </div>
          <p className="text-[10px] text-gray-600 mt-1.5 text-right">
            {results.length} of {available.length} · ↑↓ navigate · ↵ select
          </p>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-auto">
          {results.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              {query ? (
                <span>
                  No items match "{query}"{" "}
                  <button
                    onClick={() => setQuery("")}
                    className="text-orange-400 ml-1 underline"
                  >
                    clear
                  </button>
                </span>
              ) : available.length === 0 ? (
                "All labour items are already on this project"
              ) : (
                "No labour items in the catalogue yet"
              )}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-900 sticky top-0 z-10 border-b border-gray-700/70">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wide w-32">
                    Code
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wide">
                    Description
                  </th>
                  <th className="px-3 py-2.5 text-center text-[10px] text-gray-500 font-medium uppercase tracking-wide w-16">
                    UOM
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wide w-24">
                    Cust. Rate
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {results.map((item, idx) => {
                  const isActive = idx === activeIdx;
                  return (
                    <tr
                      key={item._id}
                      ref={(el) => {
                        rowRefs.current[idx] = el;
                      }}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => onSelect(item)}
                      className={cls(
                        "cursor-pointer transition-colors",
                        isActive
                          ? "bg-orange-500/10 border-l-2 border-l-orange-500"
                          : idx % 2 === 0
                            ? "bg-gray-900/40 hover:bg-gray-800/60"
                            : "bg-gray-800/20 hover:bg-gray-800/60",
                      )}
                    >
                      <td className="px-3 py-2.5 font-mono">
                        <span
                          className={cls(
                            "text-[11px] font-medium",
                            isActive ? "text-orange-300" : "text-gray-300",
                          )}
                        >
                          <Highlight
                            text={item.itemCode || "—"}
                            tokens={queryTokens}
                          />
                        </span>
                        {item.sectionNumber && (
                          <p className="text-[10px] text-gray-600 mt-0.5">
                            {item.sectionNumber}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="text-gray-100 text-[12px] leading-snug font-medium">
                          <Highlight
                            text={item.title || "—"}
                            tokens={queryTokens}
                          />
                        </p>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400">
                          {item.uom || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-300">
                        ${(item.unitRate || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-700 px-4 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function LabourRatesTab({ project, setProject }) {
  const [contractLabourItems, setContractLabourItems] = useState([]);
  const [allLabourItems, setAllLabourItems] = useState([]); // company catalogue, live-resolved
  const [labourItemsLoading, setLabourItemsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add flow
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState(null); // LabourItem selected from modal
  const [pendingRate, setPendingRate] = useState(0);

  // Edit flow (inline)
  const [editingItemId, setEditingItemId] = useState(null);
  const [editForm, setEditForm] = useState({ unitRate: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // itemId awaiting delete confirm

  // Cost helper — prefers live-resolved value from the GET endpoint
  const liveUnitCost = (item) =>
    Number(item.liveUnitCost ?? item.unitCost ?? 0);

  // ── Load contract items ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API}/api/projects/${project._id}/contract-labour-items`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setContractLabourItems(res.data);
      } catch (err) {
        setError(
          "Failed to load labour rates: " +
            (err.response?.data?.message || err.message),
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [project._id]);

  // ── Lazy-load company catalogue ──────────────────────────────────────────────
  // /api/labour-items already returns live-resolved title/uom/section from ItemDatabase
  const fetchAllLabourItems = async () => {
    if (allLabourItems.length > 0) return;
    setLabourItemsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/labour-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllLabourItems(res.data);
    } catch (err) {
      setError(
        "Failed to load labour catalogue: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLabourItemsLoading(false);
    }
  };

  const openModal = async () => {
    await fetchAllLabourItems();
    setModalOpen(true);
  };

  // IDs of LabourItems already on the project — used to filter the modal
  const alreadyAddedIds = useMemo(
    () =>
      new Set(
        contractLabourItems
          .map((ci) => (ci.labourItem?._id || ci.labourItem || "").toString())
          .filter(Boolean),
      ),
    [contractLabourItems],
  );

  // ── Add handlers ─────────────────────────────────────────────────────────────
  const handleSelectFromModal = (labourItem) => {
    setPendingItem(labourItem);
    setPendingRate(labourItem.unitRate || 0);
    setModalOpen(false);
    setEditingItemId(null);
    setError(null);
  };

  const handleConfirmAdd = async () => {
    if (!pendingItem) return;
    if (pendingRate < 0) {
      setError("Customer rate cannot be negative.");
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/projects/${project._id}/contract-labour-items`,
        { labourItem: pendingItem._id, unitRate: parseFloat(pendingRate) || 0 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setContractLabourItems((prev) => [...prev, response.data]);
      setProject((prev) => ({
        ...prev,
        contractLabourItems: [
          ...(prev.contractLabourItems || []),
          response.data,
        ],
      }));
      setPendingItem(null);
      setError(null);
    } catch (err) {
      setError(
        "Failed to add item: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Edit handlers ─────────────────────────────────────────────────────────────
  const startEditing = (item) => {
    setEditingItemId(item._id);
    setConfirmDeleteId(null);
    setPendingItem(null);
    setError(null);
    setEditForm({ unitRate: item.unitRate ?? 0 });
  };

  const handleEdit = async (itemId) => {
    if (editForm.unitRate < 0) {
      setError("Customer rate cannot be negative.");
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/api/projects/${project._id}/contract-labour-items/${itemId}`,
        { unitRate: parseFloat(editForm.unitRate) || 0 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Preserve live-resolved fields the PUT response doesn't include
      setContractLabourItems((prev) =>
        prev.map((item) =>
          item._id === itemId
            ? {
                ...response.data,
                liveUnitCost: item.liveUnitCost,
                liveCostResolved: item.liveCostResolved,
                title: item.title,
                uom: item.uom,
                sectionNumber: item.sectionNumber,
              }
            : item,
        ),
      );
      setProject((prev) => ({
        ...prev,
        contractLabourItems: (prev.contractLabourItems || []).map((item) =>
          item._id === itemId ? response.data : item,
        ),
      }));
      setEditingItemId(null);
      setError(null);
    } catch (err) {
      setError(
        "Failed to update rate: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingItemId(null);
    setConfirmDeleteId(null);
    setPendingItem(null);
    setError(null);
    setEditForm({ unitRate: 0 });
  };

  // ── Delete handler ────────────────────────────────────────────────────────────
  const handleDelete = async (itemId) => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/api/projects/${project._id}/contract-labour-items/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setContractLabourItems((prev) => prev.filter((ci) => ci._id !== itemId));
      setProject((prev) => ({
        ...prev,
        contractLabourItems: (prev.contractLabourItems || []).filter(
          (ci) => (ci._id || ci) !== itemId,
        ),
      }));
      setEditingItemId(null);
      setConfirmDeleteId(null);
      setError(null);
    } catch (err) {
      setError(
        "Failed to remove item: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Shared UI ─────────────────────────────────────────────────────────────────
  const Spinner = ({ size = "sm" }) => (
    <svg
      className={cls("animate-spin", size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4")}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 flex items-center justify-center gap-2">
        <Spinner size="md" />
        <span className="text-gray-300 text-sm">Loading labour rates…</span>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <div className="px-6 py-5 border-b border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white tracking-tight">
            Labour Rates
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Code, description &amp; UOM are live from the item database · only
            the customer rate is stored per project
          </p>
        </div>
        {!pendingItem && (
          <button
            onClick={openModal}
            disabled={labourItemsLoading}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-900 bg-orange-400 hover:bg-orange-500 transition-colors shadow-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {labourItemsLoading ? (
              <Spinner />
            ) : (
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
            Add Item
          </button>
        )}
      </div>

      {/* ── Error banner ────────────────────────────────────────────────────────── */}
      {error && (
        <div className="mx-6 mt-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
          <svg
            className="h-4 w-4 mt-0.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-300 text-xs shrink-0"
          >
            dismiss
          </button>
        </div>
      )}

      {/* ── Pending-add panel ───────────────────────────────────────────────────── */}
      {pendingItem && (
        <div className="mx-6 mt-4 p-4 rounded-lg bg-gray-900/60 border border-orange-500/20">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Item info */}
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-1.5">
                Adding to project
              </p>
              <p className="font-mono text-[12px] font-medium text-orange-400">
                {pendingItem.itemCode}
              </p>
              <p className="text-sm text-gray-100 font-medium mt-0.5 leading-snug">
                {pendingItem.title}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
                  {pendingItem.uom}
                </span>
                {pendingItem.sectionNumber && (
                  <span className="text-[10px] text-gray-500">
                    {pendingItem.sectionNumber}
                  </span>
                )}
              </div>
            </div>

            {/* Rate + actions */}
            <div className="shrink-0 flex flex-col items-start sm:items-end gap-3">
              <div>
                <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">
                  Customer Rate (for this project)
                </label>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 text-sm">$</span>
                  <input
                    type="number"
                    value={pendingRate}
                    onChange={(e) =>
                      setPendingRate(parseFloat(e.target.value) || 0)
                    }
                    step="0.01"
                    min="0"
                    autoFocus
                    className="w-32 text-right rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-1.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                  />
                  <span className="text-gray-600 text-[11px]">
                    / {pendingItem.uom}
                  </span>
                </div>
                <p className="text-[10px] text-gray-600 mt-1">
                  Default: ${(pendingItem.unitRate || 0).toFixed(2)} · adjust if
                  this project has a different rate
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAdd}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-gray-900 bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors"
                >
                  {isSaving && <Spinner />}
                  {isSaving ? "Adding…" : "Confirm Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto mt-2">
        <table className="min-w-full">
          <thead>
            <tr className="text-left border-b border-gray-700/60">
              {["Code", "Description", "UOM", "Cost", "Customer Rate", ""].map(
                (h, i) => (
                  <th
                    key={i}
                    className={cls(
                      "px-6 py-3 text-[10px] font-medium text-gray-500 uppercase tracking-wider",
                      ["Cost", "Customer Rate", "Margin"].includes(h)
                        ? "text-right"
                        : "",
                    )}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {contractLabourItems.length === 0 && !pendingItem && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <p className="text-gray-400 text-sm">
                    No labour items yet. Click{" "}
                    <span className="text-orange-400 font-medium">
                      Add Item
                    </span>{" "}
                    to get started.
                  </p>
                </td>
              </tr>
            )}

            {contractLabourItems.map((item) => {
              const isEditing = editingItemId === item._id;
              const cost = liveUnitCost(item);
              const rate = Number(item.unitRate || 0);
              const margin = rate - cost;
              const marginPct = rate > 0 ? (margin / rate) * 100 : 0;

              // Live fields resolved by the GET endpoint
              const title = item.title || item.labourItem?.title || "—";
              const itemCode =
                item.itemCode || item.labourItem?.itemCode || "—";
              const uom = item.uom || item.labourItem?.uom || "—";
              const sectionNum = item.sectionNumber || "";

              return (
                <tr
                  key={item._id}
                  onClick={() =>
                    !isEditing && !pendingItem && startEditing(item)
                  }
                  className={cls(
                    "border-t border-gray-700/70 transition-colors",
                    isEditing
                      ? "bg-gray-900/50"
                      : pendingItem
                        ? "opacity-50 pointer-events-none"
                        : "hover:bg-gray-700/40 cursor-pointer",
                  )}
                >
                  {/* Code + section */}
                  <td className="px-6 py-4 text-sm align-top">
                    <span className="font-mono font-medium text-orange-400 text-[12px]">
                      {itemCode}
                    </span>
                    {sectionNum && (
                      <p className="text-[10px] text-gray-600 mt-0.5 font-mono">
                        {sectionNum}
                      </p>
                    )}
                  </td>

                  {/* Description — always live from ItemDatabase via GET */}
                  <td className="px-6 py-4 text-sm align-top">
                    <span className="font-medium text-gray-100">{title}</span>
                  </td>

                  {/* UOM — live */}
                  <td className="px-6 py-4 text-sm align-top">
                    <span className="text-xs font-medium bg-gray-700/60 text-gray-300 px-2 py-0.5 rounded">
                      {uom}
                    </span>
                  </td>

                  {/* Cost — live from ItemDatabase */}
                  <td className="px-6 py-4 text-sm text-right align-top">
                    <span className="inline-flex items-center gap-1.5 text-gray-200">
                      ${cost.toFixed(2)}
                      {item.liveCostResolved ? (
                        <span
                          className="text-[10px] uppercase tracking-wide text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded"
                          title="Live cost from item database"
                        >
                          live
                        </span>
                      ) : (
                        <span
                          className="text-[10px] uppercase tracking-wide text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded"
                          title="Item code not found in database — showing stored value"
                        >
                          stored
                        </span>
                      )}
                    </span>
                  </td>

                  {/* Customer rate — stored per-project, editable inline */}
                  <td className="px-6 py-4 text-sm text-right align-top">
                    {isEditing ? (
                      <div className="flex items-center justify-end">
                        <span className="text-gray-400 mr-1">$</span>
                        <input
                          type="number"
                          value={editForm.unitRate}
                          onChange={(e) =>
                            setEditForm({
                              unitRate: parseFloat(e.target.value) || 0,
                            })
                          }
                          onClick={(e) => e.stopPropagation()}
                          step="0.01"
                          min="0"
                          autoFocus
                          className="w-28 text-right rounded-lg bg-gray-700 border border-gray-600 text-gray-100 px-3 py-1.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                        />
                      </div>
                    ) : (
                      <span className="text-gray-100 font-medium">
                        ${rate.toFixed(2)}
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-sm text-right align-top whitespace-nowrap">
                    {isEditing ? (
                      confirmDeleteId === item._id ? (
                        /* ── Delete confirm ── */
                        <div className="flex justify-end items-center gap-2">
                          <span className="text-[11px] text-red-300 mr-1">
                            Remove from project?
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(null);
                            }}
                            disabled={isDeleting}
                            className="px-2.5 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item._id);
                            }}
                            disabled={isDeleting}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                          >
                            {isDeleting && <Spinner />}
                            {isDeleting ? "Removing…" : "Yes, remove"}
                          </button>
                        </div>
                      ) : (
                        /* ── Save / Cancel / Delete ── */
                        <div className="flex justify-end items-center gap-2">
                          {/* Delete trigger */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(item._id);
                            }}
                            disabled={isSaving}
                            className="mr-1 text-[11px] font-medium text-red-500 hover:text-red-400 transition-colors disabled:opacity-40"
                          >
                            Remove
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item._id);
                            }}
                            disabled={isSaving}
                            className={cls(
                              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-900 transition-colors",
                              isSaving
                                ? "bg-gray-600 cursor-not-allowed opacity-50"
                                : "bg-orange-400 hover:bg-orange-500",
                            )}
                          >
                            {isSaving && <Spinner />}
                            {isSaving ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel();
                            }}
                            disabled={isSaving}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 border border-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(item);
                        }}
                        className="text-orange-400 hover:text-orange-300 text-xs font-medium transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Catalogue modal ─────────────────────────────────────────────────────── */}
      <LabourCatalogModal
        open={modalOpen}
        labourItems={allLabourItems}
        alreadyAddedIds={alreadyAddedIds}
        onSelect={handleSelectFromModal}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
